import React, {useEffect, useState} from 'react';
import {useParams, Link as RouterLink} from 'react-router-dom';
import {
    Box, Typography, Paper, CircularProgress, Grid, Chip, Button,
    Alert, List, ListItem, ListItemText, ListItemIcon, Divider, Link,
    Stack
} from '@mui/material';
import VideoWithTimestamp from '../../components/VideoWithTimestamp';
import LogsCard from '../../components/LogsCard';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import ComputerIcon from '@mui/icons-material/Computer';
import HubIcon from '@mui/icons-material/Hub';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BuildIcon from '@mui/icons-material/Build';
import apiService from '../../services/apiService';
import {getStatusColor, getWinningColor, formatPlayerName} from '../../utils/formatters';
import {useAuthDisabled} from "../../context/AuthDisabledContext";
import {ROLES} from "../../config/roles";
import PrizeItemsPage from "../prizes/PrizeItemsPage";
import PlayersPage from "../players/PlayersPage";
import Protected from "../../components/Protected";

// --- Reusable Sub-Components for a Cleaner Layout ---

const DetailsCard = ({status, winning, coins}) => (
    <Paper elevation={2} sx={{p: 2, height: '100%'}}>
        <Typography variant="h6" gutterBottom>Details</Typography>
        <Divider sx={{mb: 2}}/>
        <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography component="strong">Status:</Typography>
                <Chip label={status} color={getStatusColor(status)} size="small"/>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography component="strong">Result:</Typography>
                <Chip label={winning || 'N/A'} color={getWinningColor(winning)} size="small"/></Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography component="strong">Coins:</Typography>
                <Typography>{coins}</Typography>
            </Stack>
        </Stack>
    </Paper>
);

const TimestampsCard = ({startedAt, finishedAt}) => {
    const formatDuration = (start, end) => {
        if (!start || !end) return 'N/A';
        const seconds = Math.floor((new Date(end) - new Date(start)) / 1000);
        if (seconds < 60) return `${seconds} sec`;
        return `${Math.floor(seconds / 60)} min ${seconds % 60} sec`;
    };
    return (
        <Paper elevation={2} sx={{p: 2, height: '100%'}}>
            <Typography variant="h6" gutterBottom>Timestamps</Typography>
            <Divider sx={{mb: 2}}/>
            <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography
                    component="strong">Started:</Typography><Typography
                    variant="body2">{new Date(startedAt).toLocaleString()}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography
                    component="strong">Finished:</Typography><Typography
                    variant="body2">{finishedAt ? new Date(finishedAt).toLocaleString() : '—'}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography
                    component="strong">Duration:</Typography><Typography
                    variant="body2">{formatDuration(startedAt, finishedAt)}</Typography></Stack>
            </Stack>
        </Paper>
    );
};

const RelationsCard = ({player, machineId, gameSessionId, rewardId}) => (
    <Paper elevation={2} sx={{p: 2, height: '100%'}}>
        <Typography variant="h6" gutterBottom>Relations</Typography>
        <Divider sx={{mb: 1}}/>
        <List dense>
            <ListItem><ListItemIcon><PersonIcon/></ListItemIcon><ListItemText primary="Player"
                                                                              secondary={<Link component={RouterLink}
                                                                                               to={`/players/${player.id}`}>{formatPlayerName(player, false)}</Link>}/></ListItem>
            <ListItem><ListItemIcon><ComputerIcon/></ListItemIcon><ListItemText primary="Machine"
                                                                                secondary={<Link component={RouterLink}
                                                                                                 to={`/machines/${machineId}`}>{machineId}</Link>}/></ListItem>
            <ListItem><ListItemIcon><HubIcon/></ListItemIcon><ListItemText primary="Session"
                                                                           secondary={<Link component={RouterLink}
                                                                                            to={`/game-sessions/${gameSessionId}`}>{gameSessionId}</Link>}/></ListItem>
            <ListItem><ListItemIcon><EmojiEventsIcon/></ListItemIcon><ListItemText primary="Reward"
                                                                                   secondary={rewardId ?
                                                                                       <Link component={RouterLink}
                                                                                             to={`/rewards/${rewardId}`}>{rewardId}</Link> : '—'}/></ListItem>
        </List>
    </Paper>
);

const ActionsCard = () => {
    const isDisabled = useAuthDisabled();

    return (
        <Paper elevation={2} sx={{p: 2}}>
            <Typography variant="h6" gutterBottom>Admin Actions</Typography>
            <Divider sx={{mb: 2}}/>
            <Grid container spacing={1}>
                <Grid size={{xs: 12, sm: "auto"}}>
                    <Button fullWidth variant="contained" disabled={isDisabled} color="warning" startIcon={<CancelIcon/>}>
                        Cancel Award
                    </Button>
                </Grid>
                <Grid size={{xs: 12, sm: "auto"}}>
                    <Button fullWidth variant="contained" disabled={isDisabled} color="success" startIcon={<CheckCircleIcon/>}>
                        Assign Reward
                    </Button>
                </Grid>
                <Grid size={{xs: 12, sm: "auto"}}>
                    <Button fullWidth variant="contained" disabled={isDisabled} color="error" startIcon={<BuildIcon/>}>
                        Fix Error State
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );
}

const GameRecordingsCard = ({ gameId }) => {
    // State to hold the map of recordings: { "main": { "video": "...", "thumbnail": "..." } }
    const [recordings, setRecordings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!gameId) return;

        apiService.get(`/api/games/${gameId}/video`)
            .then(data => {
                setRecordings(data);
            })
            .catch(err => {
                if (err.status === 404) {
                    setRecordings({});
                } else {
                    setError(err.message || 'Failed to load recordings.');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, [gameId]);

    const renderContent = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 150 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (error) {
            return <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>;
        }
        if (!recordings || Object.keys(recordings).length === 0) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 150, color: 'text.secondary' }}>
                    <Typography>No recordings found</Typography>
                </Box>
            );
        }

        return (
            <Grid container spacing={2} sx={{ p: 1 }}>
                {Object.entries(recordings).map(([cameraIdentifier, urls]) => (
                    urls.video && (<Grid size={{xs: 12, sm: 6}} key={cameraIdentifier}>
                        <Typography variant="subtitle2" align="center" gutterBottom>
                            {cameraIdentifier.charAt(0).toUpperCase() + cameraIdentifier.slice(1)} Camera
                        </Typography>
                        {/* Check if a valid video URL exists. If not, show a message instead of an empty player. */}
                        {urls.video ? (
                            <VideoWithTimestamp
                                src={urls.video}
                                thumbnail={urls.thumbnail}
                                recordedAt={urls.recordedAt}
                            />
                        ) : (
                            <Box sx={{
                                position: 'relative',
                                paddingTop: '56.25%',
                                bgcolor: 'grey.200',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'text.secondary'
                            }}>
                                <Typography variant="caption">Video Not Available</Typography>
                            </Box>
                        )}
                    </Grid>)
                ))}
            </Grid>
        );
    };

    return (
        <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Game Replay</Typography>
            <Divider />
            {renderContent()}
        </Paper>
    );
};

// --- Main Page Component ---

const GameDetailPage = () => {
    const {gameId} = useParams();
    const [game, setGame] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        apiService.get(`/api/games/${gameId}`)
            .then(resp => setGame(resp))
            .catch(err => setError(err.message || 'Failed to load game details.'))
            .finally(() => setLoading(false));
    }, [gameId]);

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;
    if (error) return <Alert severity="error" sx={{m: 2}}>{error}</Alert>;
    if (!game) return <Alert severity="info" sx={{m: 2}}>No such game found.</Alert>;

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>Game #{game.id}</Typography>

            <Grid container spacing={3}>
                {/* Left Column */}
                <Grid size={{xs: 12, lg: 7}}>
                    <Stack spacing={3}>
                        <Grid container spacing={3}>
                            <Grid size={{xs: 12, md: 5}}><DetailsCard {...game} /></Grid>
                            <Grid size={{xs: 12, md: 7}}><TimestampsCard {...game} /></Grid>
                        </Grid>
                        <RelationsCard {...game} />
                        <Protected component={GameDetailPage} action="update" mode="disable">
                            <ActionsCard/>
                        </Protected>
                    </Stack>
                </Grid>

                {/* Right Column — replay only; logs moved to a full-width bottom row */}
                <Grid size={{xs: 12, lg: 5}}>
                    <GameRecordingsCard gameId={game.id} />
                </Grid>

                {/* Full-width bottom row — Game Logs spans the entire screen */}
                <Grid size={12}>
                    <LogsCard
                        title="Game Logs"
                        streamUrl={`/api/games/${game.id}/logs/stream`}
                        cacheKey={`game:${game.id}`}
                        emptyText="No log entries mention this gameId."
                    />
                </Grid>

            </Grid>

            <Box sx={{mt: 3}}>
                <Button component={RouterLink} to="/games" variant="outlined" startIcon={<ArrowBackIcon/>}>
                    Back to Games
                </Button>
            </Box>
        </Box>
    );
};

PrizeItemsPage.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
    delete: [ROLES.ADMINS],
};

export default GameDetailPage;